import { Button, Input, InputGroup, Popover } from "@chakra-ui/react"
import IconButton from "@pathwright/ui/src/components/button/IconButton"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import {
  PRIMARY_GRAY,
  WARNING_ORANGE
} from "@pathwright/ui/src/components/utils/colors"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import isEmpty from "lodash/isEmpty"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import styled from "styled-components"
import ErrorMessage from "../error/ErrorMessage"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"

const PasteButtonWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${(p) => (p.pasting ? "0.55rem 2.95rem" : "0.2rem 1.2rem")};
  border-radius: 100px;
  color: #828282;
  background-color: #efefef;

  .IconButton {
    background-color: transparent;
    color: #828282;
  }

  .IconButton:hover {
    background-color: transparent;
    color: ${PRIMARY_GRAY};
  }

  ${(p) =>
    p.mode === "dark"
      ? `
    color: #fff;
    background-color: ${PRIMARY_GRAY};

    .IconButton,
    .IconButton:hover {
      color: #fff;
    }
  `
      : ``}
`

const StyledDivider = styled.div`
  display: inline-block;
  height: 100%;
`

const PasteButton = ({
  clip,
  onPaste,
  onCancel,
  error: propsError = null,
  style = {},
  styleMode = "light",
  getTooltipLabel,
  pasting: propsPasting = false,
  allowMultiplePaste = false
}) => {
  const { t } = useTranslate()

  const [pasting, setPasting] = useState(propsPasting)
  const [error, setError] = useState(null)

  const [copies, setCopies] = useState(1)
  const [copiesUIOpen, setCopiesUIOpen] = useState(false)

  // Expecting a promise returned from onPaste. Handling the error,
  // if any, and always setting paste to false.
  const handlePaste = async (e) => {
    // Top secret feature: paste multiple blocks with Cmd+Shift
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && allowMultiplePaste) {
      e.stopPropagation()
      setCopiesUIOpen(true)
      return
    }
    e.stopPropagation()
    setError(null)
    setPasting(true)
    try {
      await onPaste(clip, copies)
    } catch (pasteError) {
      setError(pasteError)
    } finally {
      setCopiesUIOpen(false)
      setPasting(false)
    }
  }

  useEffect(() => {
    let error = null

    if (clip) {
      // Check for errors
      if (isEmpty(clip) || !clip.object_label || !clip.object_id) {
        error = "The copied clip is not valid."
      } else if (`${clip.object_id}`.split(":")[0] === "undefined") {
        error = "The copied source clip is no longer available."
      }
    } else {
      setPasting(false)
    }

    const nextError = propsError || error
    setError(nextError)
    if (error) setPasting(false)
    if (propsError) setPasting(false)
  }, [clip, propsError])

  const tooltipLabel = React.useMemo(() => {
    if (!clip) return ""

    const defaultTooltip = t(`Paste {{clipLabel}}`, {
      // Just in case a "Paste " has been added to the label
      clipLabel: clip.object_label.replace("Paste ", "")
    })

    return getTooltipLabel ? getTooltipLabel(clip) : defaultTooltip
  }, [clip])

  // Don't show the button if there's nothing to paste
  if (!clip || !clip.object_label) return null

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...style
      }}
      className="PasteButton"
      onClick={(e) => e.stopPropagation()}
    >
      <PasteButtonWrapper
        className="PasteButton__inner-wrapper"
        pasting={pasting || propsPasting}
        mode={styleMode}
      >
        {!pasting && !propsPasting ? (
          <React.Fragment>
            {allowMultiplePaste ? (
              <Popover.Root
                open={copiesUIOpen}
                onOpenChange={(e) => setCopiesUIOpen(e.open)}
              >
                <Popover.Positioner>
                  <Popover.Content
                    size="xs"
                    maxW="max-content"
                    offset={[20, 20]}
                  >
                    <Popover.Arrow />
                    <Popover.Body p={2}>
                      <InputGroup
                        display="flex"
                        alignItems="center"
                        gap={1}
                        endElement={
                          <Button
                            variant="legacy.interactiveSecondary"
                            loading={pasting}
                            onClick={handlePaste}
                            size="sm"
                            px={4}
                          >
                            <Pathicon icon="paste" />
                            Paste {copies} {copies > 1 ? "times" : "time"}
                          </Button>
                        }
                      >
                        <Input
                          type="number"
                          value={copies}
                          onChange={(e) =>
                            setCopies(
                              Math.max(1, Math.min(50, Number(e.target.value)))
                            )
                          }
                          aria-label="Number of copies to paste"
                          size="sm"
                          maxW="4rem"
                          min={1}
                          max={50}
                        />
                      </InputGroup>
                    </Popover.Body>
                  </Popover.Content>
                </Popover.Positioner>
              </Popover.Root>
            ) : null}
            <Tooltip
              title={error ? <ErrorMessage error={error} /> : tooltipLabel}
              portal
            >
              <IconButton
                onClick={handlePaste}
                icon="paste"
                styleType="primary"
                style={{
                  marginRight: "0.4rem",
                  color: !!error ? WARNING_ORANGE : ""
                }}
                disabled={!!error}
              />
            </Tooltip>
            <StyledDivider>|</StyledDivider>
            <Tooltip title="Cancel paste" portal>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation()
                  onCancel()
                }}
                icon="x"
                styleType="secondary"
                style={{ marginLeft: "0.4rem" }}
              />
            </Tooltip>
          </React.Fragment>
        ) : (
          <LoadingCircle size={18} center={false} />
        )}
      </PasteButtonWrapper>
    </div>
  )
}

export const ClipShape = PropTypes.shape({
  object_label: PropTypes.string.isRequired,
  object_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  object_type: PropTypes.string.isRequired,
  data: PropTypes.object
})

PasteButton.propTypes = {
  onPaste: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  clip: ClipShape,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  style: PropTypes.object,
  styleMode: PropTypes.oneOf(["light", "dark"]),
  getTooltipLabel: PropTypes.func
}

export default PasteButton
